import Vue from "vue";
import Vuex from "vuex";

import ProjectApi from "../api/agateProjectsApi";

Vue.use(Vuex);

import CatalogsApi from "@/api/catalogsApi";
import InitialMapStateApi from "@/api/initialMapStateApi";
import SpatialSearchApi from "@/api/spatialSearchApi";
import LayerCatalogsConverter from "@/models/layerCatalogsConverter";
import RasterLayersFinder from "@/models/rasterLayersFinder";
import { getFeatureByWfs } from "@/api/geoserver/owsFeatureSearch";
import Feature from "@/models/feature";
import SearchResult from "@/models/searchResult";
import MapConfig from "@/api/mapConfigApi";
import IsogdSectionsApi from "../api/isogdSectionsApi";
import PortalSettingsApi from "../api/portalSettingsApi";
import UserApi from "../api/userApi";

export default new Vuex.Store({
  state: {
    isAuth: false,
    user: undefined,
    portalSettings: null,
    mapConfig: null,
    currentProject: null,
    projects: [],
    rootCatalog: null,
    allLayers: null,
    activeBackgroundLayer: null,
    searchResult: null,
    searchLocation: null,
    highlightedGeometry: null,
    activeDetailComponent: null,
    mapSearchLoading: false,
    gisogdSections: [],
  },

  getters: {
    visibleLayers: (state) => {
      return state.allLayers.filter(
        (layer) => layer.visible || layer.infoSetKeys
      );
    },
    portalName: (state) => state.portalSettings.portalName,
    portalLogoBase64: (state) =>
      state.portalSettings.logo
        ? `data:${state.portalSettings.logo.fileType};base64,${
            state.portalSettings.logo.content
          }`
        : "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTYuMTkyMDQgMjRMMjQgMjRMMjQgNi4zNzg3M0w2LjE5MjA0IDI0WiIgZmlsbD0iIzBCOTA5NiIvPgo8cGF0aCBkPSJNMS43MTY1NWUtMDYgMS45MDc5OGUtMDdMMjQgMEwxOC43NDAzIDUuMDMyMjZMMi4xNTY0OGUtMDYgNS4wMzIyNkwxLjcxNjU1ZS0wNiAxLjkwNzk4ZS0wN1oiIGZpbGw9IiMwQjkwOTYiLz4KPHBhdGggZD0iTTIuMzkzMzdlLTA2IDcuNzQxOTRMMTUuODcxIDcuNzQxOTRMMTEuMjI1OCAxMkwyLjc2NTYyZS0wNiAxMkwyLjM5MzM3ZS0wNiA3Ljc0MTk0WiIgZmlsbD0iIzBCOTA5NiIvPgo8cGF0aCBkPSJNMy44MTQ3ZS0wNiAyNEwzLjA0ODYxZS0wNiAxNS4yMzdMOC45MDE1MiAxNS4yMzdMMy44MTQ3ZS0wNiAyNFoiIGZpbGw9IiMwQjkwOTYiLz4KPC9zdmc+Cg==",
  },

  mutations: {
    SET_MAP_CONFIG(state, payload) {
      state.mapConfig = payload;
    },
    SET_AUTH_STATE(state, payload) {
      state.isAuth = payload;
    },
    SET_USER_INFO(state, payload) {
      state.user = payload;
    },
    SET_PROJECTS(state, payload) {
      state.projects = payload;
    },

    SET_ISOGD_SECTIONS(state, payload) {
      state.gisogdSections = payload;
    },

    SET_CURRENT_PROJECT(state, payload) {
      state.currentProject = payload;
    },

    SET_ROOT_CATALOG(state, payload) {
      state.rootCatalog = payload;
      state.allLayers = RasterLayersFinder.fromCatalog(state.rootCatalog);
    },

    SET_SEARCH_RESULT(state, payload) {
      state.searchResult = payload;
    },

    SET_ACTIVE_DETAIL_COMPONENT(state, payload) {
      state.activeDetailComponent = payload;
    },

    SET_SEARCH_LOCATION(state, payload) {
      state.searchLocation = payload;
    },

    SET_HIGHLIGHTED_GEOMETRY(state, payload) {
      state.highlightedGeometry = payload;
    },

    TOGGLE_LAYER(state, payload) {
      const foundLayerCatalog = state.allLayers.filter(
        (lc) => lc.name === payload
      );
      foundLayerCatalog.forEach((lc) => (lc.visible = !lc.visible));
    },

    SET_LAYER_VISIBILITY(state, payload) {
      const layer = state.allLayers.find((lc) => lc.name === payload.layerName);
      layer.visible = payload.visible;
    },

    SET_LAYERS_VISIBILITY(state, payload) {
      for (let i = payload.layers.length - 1; i >= 0; i--) {
        const layer = state.allLayers.find(
          (lc) => lc.name === payload.layers[i].name
        );
        setTimeout(() => {
          if (!payload.visible) {
            layer.infoSetKeys = null;
            layer.visible = false;
          } else {
            if (
              payload.layers[i].infoSetKeys &&
              payload.layers[i].infoSetKeys.length > 0
            )
              layer.infoSetKeys = payload.layers[i].infoSetKeys;
            else layer.infoSetKeys = null;
            if (!layer.infoSetKeys) layer.visible = payload.visible;
          }
        }, 0);
      }
    },
    /*
		SET_LAYER_OPACITY(state, payload) {
			const layer = state.allLayers.find(lc => lc.name === payload.layerName);
			layer.opacity = payload.opacity;
		},*/

    TURN_OFF_ALL_LAYERS(state) {
      state.allLayers.forEach((lc) => (lc.visible = false));
    },

    SET_MAP_SEARCH_LOADING(state, payload) {
      state.mapSearchLoading = payload;
    },
    SET_ACTIVE_BACKGROUND_LAYER(state, payload) {
      state.activeBackgroundLayer = payload;
    },
    SET_PORTAL_SETTINGS(state, payload) {
      state.portalSettings = payload;
    },
  },

  actions: {
    fetchProjectsAsync: ({ commit }) => {
      return ProjectApi.get().then((projects) => {
        if (projects.length === 0) throw new Error("No projects fetched.");
        commit("SET_PROJECTS", projects);
      });
    },
    fetchIsogdSections: ({ commit }, projectSchema) => {
      return IsogdSectionsApi.get(projectSchema).then((gisogdSections) => {
        if (gisogdSections.length === 0)
          throw new Error("No isogd sections fetched.");
        commit("SET_ISOGD_SECTIONS", gisogdSections);
      });
    },
    setCurrentProject: ({ commit, state }, projectSchema) => {
      const currentProject = state.projects.find(
        (p) => p.schema === projectSchema
      );
      commit("SET_CURRENT_PROJECT", currentProject);
    },

    async fetchCatalogsAsync({ commit, state }) {
      const projectSchema = state.currentProject.schema;
      const formatUrlTilePrefix =
        this.mapConfig &&
        this.mapConfig.leafletSubDomains &&
        this.mapConfig.leafletSubDomains.prefix
          ? window.origin.replace(
              "://",
              "://" + this.mapConfig.leafletSubDomains.prefix
            )
          : "";
      const formatUrlTile = (projectSchema, layerName) =>
        `${formatUrlTilePrefix}/map/tiles/${projectSchema}/gwc/service/wmts?layer=${layerName}&width={tileSize}&height={tileSize}&style=&tilematrixset=EPSG:3857&TileMatrix=EPSG:3857:{z}&Service=WMTS&Request=GetTile&Version=1.0.0&Format=image/png&TileCol={x}&TileRow={y}`;
      const rootDto = await CatalogsApi.getRoot(projectSchema);

      LayerCatalogsConverter.fromDto(projectSchema, formatUrlTile, rootDto);
      commit("SET_ROOT_CATALOG", rootDto);
    },

    async searchByPointAsync({ commit, state }, point) {
      commit("SET_ACTIVE_DETAIL_COMPONENT", null);
      commit("SET_MAP_SEARCH_LOADING", true);
      commit("SET_SEARCH_LOCATION", point);
      commit("SET_HIGHLIGHTED_GEOMETRY", null);
      const projectSchema = state.currentProject.schema;
      const layerNames = [];
      const dtos = await SpatialSearchApi.getDocsByPoint(
        projectSchema,
        state.searchLocation.point.x,
        state.searchLocation.point.y,
        layerNames
      );
      if (!dtos) commit("SET_SEARCH_RESULT", SearchResult.Empty());
      else
        commit(
          "SET_SEARCH_RESULT",
          new SearchResult(dtos.gisogdDocGeoFeatures, dtos.orphanGeoFeatures)
        );
      commit("SET_MAP_SEARCH_LOADING", false);
    },

    async setActiveGeoFeature({ commit }, geoFeature) {
      const geoJson = await getFeatureByWfs(geoFeature.layerName, [
        geoFeature.key,
      ]);
      commit("SET_ACTIVE_DETAIL_COMPONENT", {
        type: "GeoFeatureProperties",
        data: {
          feature: new Feature(
            geoJson.features[0],
            geoFeature.layerName,
            geoFeature.layerDisplayName
          ),
        },
      });
    },

    setActiveGroup({ commit }, obj) {
      commit("SET_ACTIVE_DETAIL_COMPONENT", {
        type: "Group",
        data: { group: obj },
      });
    },

    setActiveIsogdDoc({ commit }, obj) {
      commit("SET_ACTIVE_DETAIL_COMPONENT", {
        type: "GisogdDocProperties",
        data: { doc: obj },
      });
    },

    setHighlightedGeometry({ commit }, geoJsonGeometry) {
      commit("SET_HIGHLIGHTED_GEOMETRY", geoJsonGeometry);
    },

    resetActiveDetailObject({ commit }) {
      commit("SET_ACTIVE_DETAIL_COMPONENT", null);
    },

    reset({ commit }) {
      commit("SET_SEARCH_LOCATION", null);
      commit("SET_SEARCH_RESULT", null);
      commit("SET_ACTIVE_DETAIL_COMPONENT", null);
    },

    toggleLayer({ commit }, layerName) {
      commit("TOGGLE_LAYER", layerName);
    },

    turnOffAllLayers({ commit }) {
      commit("TURN_OFF_ALL_LAYERS");
    },

    setLayersVisibility({ commit }, payload) {
      commit("SET_LAYERS_VISIBILITY", payload);
    },
    /*
		setLayerOpacity({commit}, payload) {
			commit('SET_LAYER_OPACITY', payload);
		},*/

    async fetchMapConfigAsync({ commit, state }, payload) {
      const projectSchema = state.currentProject.schema;
      const mapConfig = await MapConfig.get(projectSchema);
      let initMapState = null;
      if (payload && payload.gisogdSection) {
        initMapState = await InitialMapStateApi.getByIsogdDoc(
          projectSchema,
          payload.gisogdSection,
          payload.docKey,
          payload.onlyDocument
        );
        commit("SET_LAYERS_VISIBILITY", {
          layers: initMapState.layers,
          visible: true,
        });
        if (initMapState.selectedGeoJson)
          commit("SET_HIGHLIGHTED_GEOMETRY", initMapState.selectedGeoJson);
        mapConfig.initialBounds = initMapState.bounds;
      }
      commit("SET_MAP_CONFIG", mapConfig);
      if (mapConfig.backgroundLayers.length)
        commit("SET_ACTIVE_BACKGROUND_LAYER", mapConfig.backgroundLayers[0]);
      else commit("SET_ACTIVE_BACKGROUND_LAYER", null);
    },

    async fetchPortalSettingsAsync({ commit }) {
      const portalSettings = await PortalSettingsApi.get();
      commit("SET_PORTAL_SETTINGS", portalSettings);
    },

    async fetchUserInfoAsync({ commit }) {
      const userInfo = await UserApi.getUserInfo();
      commit("SET_USER_INFO", userInfo);
    },
    async fetchAuthStateAsync({ commit }) {
      const isAuth = await UserApi.checkSession();
      commit("SET_AUTH_STATE", isAuth);
    },
    setActiveBackgroundLayer({ commit }, payload) {
      commit("SET_ACTIVE_BACKGROUND_LAYER", payload);
    },
  },
});
